if (!customElements.get("option-image-picker")) {
  customElements.define(
    "option-image-picker",
    class OptionImagePicker extends HTMLElement {
      constructor() {
        super();
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onBackdropClick = this.onBackdropClick.bind(this);
        this._ported = false;
      }

      connectedCallback() {
        this.select = this.querySelector("select");
        this.trigger = this.querySelector(".m-option-picker__trigger");
        this.triggerMedia = this.querySelector("[data-option-picker-media]");
        this.triggerLabel = this.querySelector("[data-option-picker-label]");
        this.dropdown = this.querySelector(".m-option-picker__dropdown");
        this.backdrop = this.querySelector("[data-option-picker-backdrop]");
        this.searchInput = this.querySelector(".m-option-picker__search");
        this.emptyState = this.querySelector(".m-option-picker__empty");
        this.closeButton = this.querySelector("[data-option-picker-close]");
        this.optionButtons = Array.from(this.querySelectorAll(".m-option-picker__option"));

        if (this.select) {
          this.select.setAttribute("tabindex", "-1");
          this.select.setAttribute("aria-hidden", "true");
        }

        this.trigger.addEventListener("click", this.onTriggerClick);
        this.backdrop?.addEventListener("click", this.onBackdropClick);
        this.closeButton?.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.toggle(false);
        });

        this.searchInput.addEventListener("input", () => this.filter());
        this.searchInput.addEventListener("keydown", (event) => {
          if (event.key === "Enter") event.preventDefault();
        });

        this.optionButtons.forEach((button) => {
          button.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.choose(button);
          });
        });

        this.querySelectorAll(".m-option-picker__image[data-fallback-png]").forEach((image) => {
          image.addEventListener("error", () => this.handleImageError(image));
          image.addEventListener("load", () => this.handleImageLoad(image));
        });

        this.syncFromSelect();
      }

      disconnectedCallback() {
        this.teardownPortal();
        document.removeEventListener("click", this.onDocumentClick);
        document.removeEventListener("keydown", this.onKeyDown);
        document.body.classList.remove("m-option-picker-lock");
      }

      get useSheet() {
        return window.matchMedia("(max-width: 989px), (hover: none), (pointer: coarse)").matches;
      }

      onTriggerClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.toggle();
      }

      onBackdropClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.toggle(false);
      }

      toggle(force) {
        const open = typeof force === "boolean" ? force : this.dropdown.hidden;
        this.trigger.setAttribute("aria-expanded", String(open));
        this.classList.toggle("is-open", open);

        if (open) {
          this.openPanel();
        } else {
          this.closePanel();
        }
      }

      openPanel() {
        this.searchInput.value = "";
        this.filter();

        if (this.useSheet) {
          this.mountPortal();
          this.dropdown.classList.add("m-option-picker__dropdown--sheet");
          this.backdrop.hidden = false;
          this.backdrop.classList.add("is-visible");
          document.body.classList.add("m-option-picker-lock");
        } else {
          this.dropdown.classList.remove("m-option-picker__dropdown--sheet");
          if (this.backdrop) {
            this.backdrop.hidden = true;
            this.backdrop.classList.remove("is-visible");
          }
        }

        this.dropdown.hidden = false;

        if (!this.useSheet) {
          requestAnimationFrame(() => this.searchInput.focus({ preventScroll: true }));
        } else if (this.searchInput === document.activeElement) {
          this.searchInput.blur();
        }

        clearTimeout(this._bindOutsideClick);
        this._bindOutsideClick = setTimeout(() => {
          document.addEventListener("click", this.onDocumentClick);
          document.addEventListener("keydown", this.onKeyDown);
        }, 0);
      }

      closePanel() {
        clearTimeout(this._bindOutsideClick);
        document.removeEventListener("click", this.onDocumentClick);
        document.removeEventListener("keydown", this.onKeyDown);

        this.dropdown.hidden = true;
        this.dropdown.classList.remove("m-option-picker__dropdown--sheet");

        if (this.backdrop) {
          this.backdrop.hidden = true;
          this.backdrop.classList.remove("is-visible");
        }

        this.teardownPortal();
        document.body.classList.remove("m-option-picker-lock");

        if (this.searchInput === document.activeElement) this.searchInput.blur();
      }

      mountPortal() {
        if (this._ported) return;

        // Move sheet + backdrop to body so parent overflow/transform cannot clip them.
        this._dropdownHome = document.createComment("option-picker-dropdown");
        this._backdropHome = document.createComment("option-picker-backdrop");

        this.dropdown.parentNode.insertBefore(this._dropdownHome, this.dropdown);
        this.backdrop.parentNode.insertBefore(this._backdropHome, this.backdrop);

        document.body.appendChild(this.backdrop);
        document.body.appendChild(this.dropdown);
        this._ported = true;
      }

      teardownPortal() {
        if (!this._ported) return;

        if (this._dropdownHome?.parentNode) {
          this._dropdownHome.parentNode.insertBefore(this.dropdown, this._dropdownHome);
          this._dropdownHome.remove();
        }

        if (this._backdropHome?.parentNode) {
          this._backdropHome.parentNode.insertBefore(this.backdrop, this._backdropHome);
          this._backdropHome.remove();
        }

        this._dropdownHome = null;
        this._backdropHome = null;
        this._ported = false;
      }

      onDocumentClick(event) {
        if (this.useSheet) {
          if (event.target === this.backdrop) this.toggle(false);
          return;
        }

        if (!this.contains(event.target) && !this.dropdown.contains(event.target)) {
          this.toggle(false);
        }
      }

      onKeyDown(event) {
        if (event.key === "Escape") {
          this.toggle(false);
          this.trigger.focus({ preventScroll: true });
        }
      }

      filter() {
        const query = this.searchInput.value.trim().toLowerCase();
        let visibleCount = 0;

        this.dropdown.querySelectorAll(".m-option-picker__item").forEach((item) => {
          const match = !query || (item.dataset.optionLabel || "").includes(query);
          item.hidden = !match;
          if (match) visibleCount += 1;
        });

        if (this.emptyState) this.emptyState.hidden = visibleCount > 0;
      }

      choose(button) {
        const value = button.dataset.value || "";
        this.select.value = value;
        this.select.dispatchEvent(new Event("change", { bubbles: true }));
        this.updateTrigger(button);
        this.optionButtons.forEach((option) => {
          option.setAttribute("aria-selected", String(option === button));
        });
        this.toggle(false);
        this.trigger.focus({ preventScroll: true });
      }

      syncFromSelect() {
        const selected =
          this.optionButtons.find((button) => button.dataset.value === this.select.value) ||
          (!this.select.value ? null : this.optionButtons[0]);

        if (selected) {
          if (!this.select.value) this.select.value = selected.dataset.value;
          this.updateTrigger(selected);
          selected.setAttribute("aria-selected", "true");
        }
      }

      updateTrigger(button) {
        this.triggerLabel.textContent = button.dataset.label || "";
        const sourceImage = button.querySelector(".m-option-picker__image");
        this.triggerMedia.innerHTML = "";

        if (sourceImage && !sourceImage.hidden) {
          const clone = sourceImage.cloneNode(true);
          clone.loading = "eager";
          clone.addEventListener("error", () => this.handleImageError(clone));
          this.triggerMedia.appendChild(clone);
        }
      }

      handleImageLoad(image) {
        const option = image.closest(".m-option-picker__option");
        if (option && option.getAttribute("aria-selected") === "true") {
          this.updateTrigger(option);
        }
      }

      handleImageError(image) {
        const png = image.getAttribute("data-fallback-png");
        const webp = image.getAttribute("data-fallback-webp");

        if (png && image.src !== png && !image.dataset.triedPng) {
          image.dataset.triedPng = "true";
          image.src = png;
          return;
        }

        if (webp && image.src !== webp && !image.dataset.triedWebp) {
          image.dataset.triedWebp = "true";
          image.src = webp;
          return;
        }

        image.hidden = true;
        const option = image.closest(".m-option-picker__option");
        if (option && option.getAttribute("aria-selected") === "true") {
          this.updateTrigger(option);
        } else if (this.triggerMedia.contains(image)) {
          image.remove();
        }
      }
    }
  );
}
